import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Users, Mail, MessageSquare, CreditCard, FileText, BarChart3, TrendingUp, ArrowLeft, Lock, ChevronDown, ChevronUp, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PASS = "excell2026";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="card-dark flex items-center gap-4">
    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-blue-200/50 text-xs">{label}</p>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString('fr-CA') : value}</p>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, color, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-dark">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between">
        <h2 className="font-semibold text-white flex items-center gap-2">
          <Icon className={`w-5 h-5 ${color}`} /> {title}
        </h2>
        {open ? <ChevronUp className="w-5 h-5 text-blue-300/50" /> : <ChevronDown className="w-5 h-5 text-blue-300/50" />}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};

const AdminPage = () => {
  const [auth, setAuth] = useState(false);
  const [pass, setPass] = useState("");
  const [data, setData] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pass === ADMIN_PASS) setAuth(true);
  };

  useEffect(() => {
    if (!auth) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/analytics/admin`),
      axios.get(`${API}/subscribers`),
      axios.get(`${API}/contacts`)
    ])
      .then(([analyticsRes, subsRes, contactsRes]) => {
        setData(analyticsRes.data);
        setSubscribers(subsRes.data);
        setContacts(contactsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [auth]);

  if (!auth) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="card-dark w-full max-w-sm text-center">
          <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="font-display font-bold text-xl text-white mb-6">Panneau administrateur</h1>
          <input type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="Mot de passe"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500 outline-none text-white placeholder-blue-300/30 mb-4" />
          <button type="submit" className="btn-gold w-full">Connexion</button>
          <Link to="/" className="text-blue-300/40 text-sm mt-4 block hover:text-amber-400 transition-colors">Retour au site</Link>
        </form>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  const maxDailyViews = Math.max(...(data.daily_stats?.map(d => d.views) || [1]), 1);

  return (
    <div className="min-h-screen bg-navy-900">
      <header className="bg-navy-900/80 backdrop-blur-lg border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-blue-200/70 hover:text-amber-400 transition-colors">
            <ArrowLeft className="w-5 h-5" /> Retour au site
          </Link>
          <h1 className="font-display font-bold text-white">Panneau admin</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Visites totales" value={data.total_views} color="bg-blue-500/20 text-blue-400" />
          <StatCard icon={TrendingUp} label="Visites aujourd'hui" value={data.today_views} color="bg-emerald-500/20 text-emerald-400" />
          <StatCard icon={Users} label="Abonnés (emails)" value={data.subscribers} color="bg-amber-500/20 text-amber-400" />
          <StatCard icon={CreditCard} label="Ventes complétées" value={data.paid_transactions} color="bg-purple-500/20 text-purple-400" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Daily Chart */}
          <div className="card-dark">
            <h2 className="font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-amber-400" /> Visites (30 jours)</h2>
            <div className="flex items-end gap-1 h-40">
              {data.daily_stats?.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute -top-8 bg-white/10 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {d.date}: {d.views}
                  </div>
                  <div className="w-full bg-amber-500/70 rounded-t transition-all hover:bg-amber-400"
                    style={{ height: `${(d.views / maxDailyViews) * 100}%`, minHeight: '2px' }}></div>
                </div>
              ))}
              {(!data.daily_stats || data.daily_stats.length === 0) && <p className="text-blue-300/40 text-sm w-full text-center">Aucune donnée</p>}
            </div>
          </div>

          {/* Top Pages */}
          <div className="card-dark">
            <h2 className="font-semibold text-white mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" /> Pages les plus visitées</h2>
            <div className="space-y-3">
              {data.top_pages?.map((p, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-blue-200/70 text-sm">{p.page === "/" ? "Accueil" : p.page}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(p.count / (data.top_pages[0]?.count || 1)) * 100}%` }}></div>
                    </div>
                    <span className="text-white font-medium text-sm w-10 text-right">{p.count}</span>
                  </div>
                </div>
              ))}
              {(!data.top_pages || data.top_pages.length === 0) && <p className="text-blue-300/40 text-sm">Aucune donnée</p>}
            </div>
          </div>
        </div>

        {/* Subscribers List */}
        <Section title={`Abonnés - emails gratuits (${subscribers.length})`} icon={Mail} color="text-amber-400" defaultOpen={true}>
          {subscribers.length === 0 ? (
            <p className="text-blue-300/40 text-sm">Aucun abonné</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-blue-200/50 pb-3 font-medium">Email</th>
                    <th className="text-left text-blue-200/50 pb-3 font-medium">Document</th>
                    <th className="text-left text-blue-200/50 pb-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).map((sub, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 text-white">{sub.email}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sub.document_id?.includes("sn4") ? "bg-blue-500/20 text-blue-400" : "bg-emerald-500/20 text-emerald-400"
                        }`}>
                          {sub.document_name || sub.document_id}
                        </span>
                      </td>
                      <td className="py-3 text-blue-200/50 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Contacts List */}
        <Section title={`Messages de contact (${contacts.length})`} icon={MessageSquare} color="text-purple-400" defaultOpen={true}>
          {contacts.length === 0 ? (
            <p className="text-blue-300/40 text-sm">Aucun message</p>
          ) : (
            <div className="space-y-4">
              {contacts.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).map((msg, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <span className="text-purple-400 font-bold text-sm">{(msg.name || "?")[0].toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{msg.name}</p>
                        <p className="text-blue-300/50 text-xs">{msg.email}</p>
                      </div>
                    </div>
                    <span className="text-blue-300/40 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {msg.created_at ? new Date(msg.created_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                    </span>
                  </div>
                  <p className="text-blue-200/70 text-sm bg-white/3 rounded-lg p-3 mt-2">{msg.message}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Events & Summary */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-dark">
            <h2 className="font-semibold text-white mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> Clics et téléchargements</h2>
            <div className="space-y-3">
              {data.events?.map((e, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-blue-200/70 text-sm">{e.event}</span>
                  <span className="text-white font-medium text-sm bg-white/5 px-3 py-1 rounded">{e.count}</span>
                </div>
              ))}
              {(!data.events || data.events.length === 0) && <p className="text-blue-300/40 text-sm">Aucun événement</p>}
            </div>
          </div>

          <div className="card-dark">
            <h2 className="font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-400" /> Résumé</h2>
            <div className="space-y-4">
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span className="text-blue-200/60 text-sm">Messages de contact</span>
                <span className="text-white font-medium">{data.contacts}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span className="text-blue-200/60 text-sm">Abonnés (emails gratuits)</span>
                <span className="text-white font-medium">{data.subscribers}</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span className="text-blue-200/60 text-sm">Transactions totales</span>
                <span className="text-white font-medium">{data.transactions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200/60 text-sm">Paiements complétés</span>
                <span className="text-emerald-400 font-bold">{data.paid_transactions}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
